import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon, Loader } from 'semantic-ui-react';

import { useGetChatSessionsQuery } from './chatSessionApiSlice';
import { canEvaluate } from '../../constants';
import ScopeNote from '../../components/ScopeNote';
import { ChipRow, PhoneDateRange, RatioScore, SearchField, SquareButton, Tag, cardAction, cardActions, cardBody, cardHeader, cardMeta, cardTitle, chip, listCard, scoreColor, scoreText, SKILL_SCORE, cardStack } from '../../components/listPage';

// Filters that arrive by link rather than from this page's own controls (components/ScopeNote).
export const LINKED_FILTERS = [
  ['missed_criterion', '선택한 평가 항목을 놓친 롤플레이'],
  ['persona', '선택한 고객 페르소나의 롤플레이'],
  ['trainee', '선택한 직원의 롤플레이'],
  ['contractor', '선택한 파트너의 롤플레이'],
];

/**
 * 롤플레이 기록 on phones. The desktop table squeezed into cards put the score out of sight and
 * the first card half a screen down; this is a list built for a phone instead:
 * compact cards (customer, when/how long, score), at most the actions that apply, a slim
 * search + filter header, and infinite scroll.
 *
 * Infinite scroll keeps its place: the number of loaded pages lives in the URL (`loaded`,
 * replaced in place) and the scroll offset in sessionStorage, so opening a session and
 * pressing back lands where you were. Each page is its own cached request.
 */

const PAGE_SIZE = 15;
const KINDS = [['전체', null], ['평가', 'false'], ['연습', 'true']];
const SCROLL_KEY = 'chatsessionslist:scroll';

export const customerLabel = p => (!p ? '실제 고객' : p.int_id ? `${p.int_id}번 고객` : p.name);

export const shortDate = iso => {
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} (${'일월화수목금토'[d.getDay()]}) ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// DRF durations look like "00:12:34.5". Sessions are capped well under a day.
export const minutesLabel = duration => {
  const m = duration && duration.match(/(\d+):(\d+):(\d+)/);
  if (!m) return null;
  const mins = Number(m[1]) * 60 + Number(m[2]);
  return mins < 1 ? '1분 미만' : `${mins}분`;
};

const SessionCard = ({ s, isAdmin, onOpen, onResume, onEvaluate }) => {
  const evaluated = s.evaluation_session_count > 0;
  const showResume = !evaluated;
  // canEvaluate already requires 6+ lines and no evaluation yet.
  const showEvaluate = !s.is_practice && canEvaluate(s);
  const meta = [shortDate(s.created_at), minutesLabel(s.duration), `발화 ${s.chatlog_count}개`]
    .filter(Boolean)
    .join(' · ');
  const stop = fn => e => {
    e.stopPropagation();
    fn(s);
  };

  return (
    <div
      role={evaluated ? 'button' : undefined}
      onClick={evaluated ? () => onOpen(s) : undefined}
style={{ ...listCard, cursor: evaluated ? 'pointer' : 'default' }}
    >
      <div style={cardHeader}>
        <div style={cardTitle}>
          {customerLabel(s.persona)}
          {s.persona?.int_id && s.persona?.name && <span style={{ fontWeight: 400, color: '#86868b' }}> · {s.persona.name}</span>}
        </div>
        <Tag tone={s.is_practice ? 'gray' : 'blue'}>{s.is_practice ? '연습' : '평가'}</Tag>
        {evaluated && <Icon name="chevron right" className="centered-icon" style={{ color: '#c7c7cc', fontSize: '12px' }} />}
      </div>

      <div style={cardMeta}>{meta}</div>
      {isAdmin && (
        <div style={cardMeta}>
          {[s.trainee?.full_name || s.trainee?.username, s.trainee?.contractor?.name].filter(Boolean).join(' · ')}
        </div>
      )}

      {evaluated ? (
        <div style={cardBody}>
          {SKILL_SCORE}{' '}
          <strong style={{ color: s.latest_evaluation_percent != null ? scoreColor(s.latest_evaluation_percent) : '#86868b' }}>
            {s.latest_evaluation_percent != null ? scoreText(s.latest_evaluation_percent) : '채점 중'}
          </strong>
          {s.pk_checked > 0 && (
            <>
              {' · '}설명 정확도 <RatioScore num={s.pk_correct} den={s.pk_checked} />
            </>
          )}
        </div>
      ) : (
        !s.is_practice && <div style={{ ...cardBody, color: '#86868b' }}>평가 전</div>
      )}

      {(showResume || showEvaluate) && (
        <div style={cardActions}>
          {showResume && (
            <button type="button" style={cardAction(!showEvaluate)} onClick={stop(onResume)}>
              <Icon name="comments" className="centered-icon" />
              이어하기
            </button>
          )}
          {showEvaluate && (
            <button type="button" style={cardAction(true)} onClick={stop(onEvaluate)}>
              <Icon name="clipboard check" className="centered-icon" />
              평가 실행
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/** One page of results; its own cached request, so restoring N pages is N cache hits. */
const Chunk = ({ page, params, onLoaded, renderCard }) => {
  const { data, isError } = useGetChatSessionsQuery(
    { ...params, page },
    { refetchOnMountOrArgChange: 60 },
  );
  useEffect(() => {
    if (data) onLoaded(page, !!data.next, data.count);
  }, [data, page, onLoaded]);
  if (isError) {
    return <div style={{ textAlign: 'center', color: '#C0392B', padding: '12px' }}>불러오지 못했습니다.</div>;
  }
  if (!data) return <Loader active inline="centered" size="small" style={{ margin: '16px auto' }} />;
  return data.results.map(renderCard);
};

const MobileChatSessionList = ({ isAdmin, onOpen, onEvaluate, onUpload }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const qs = new URLSearchParams(location.search);
  const search = qs.get('search') || '';
  const startDate = qs.get('start_date');
  const endDate = qs.get('end_date');
  const kind = qs.get('is_practice');
  const loaded = Math.max(1, parseInt(qs.get('loaded') || '1', 10) || 1);

  const [searchValue, setSearchValue] = useState(search);
  const [filtersOpen, setFiltersOpen] = useState(!!(startDate || endDate));
  const [tail, setTail] = useState({ page: 0, hasNext: false, count: null });

  const params = Object.fromEntries(
    Object.entries({
      search,
      start_date: startDate,
      end_date: endDate,
      is_practice: kind,
      // Linked filters (고객 페르소나, 직원 관리, 직원 홈) — the phone list used to drop them
      // and show every session. ScopeNote says they're on.
      persona: qs.get('persona'),
      trainee: qs.get('trainee'),
      contractor: qs.get('contractor'),
      missed_criterion: qs.get('missed_criterion'),
      has_lines: 1,
      page_size: PAGE_SIZE,
    }).filter(([, v]) => v),
  );
  const filterKey = JSON.stringify(params);

  // keepPlace: only `loaded` changes (infinite scroll) — replace, don't add history.
  const setQuery = (changes, keepPlace = false) => {
    const p = new URLSearchParams(location.search);
    Object.entries(changes).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    if (!keepPlace) {
      p.delete('loaded');
      p.delete('page');
      window.scrollTo(0, 0);
    }
    navigate(`${location.pathname}?${p.toString()}`, { replace: keepPlace });
  };

  const onLoaded = useCallback(
    (page, hasNext, count) => {
      if (page === loaded) setTail({ page, hasNext, count });
    },
    [loaded],
  );

  // Back from a session: restore the scroll offset once every loaded page is in.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || tail.page !== loaded) return;
    restored.current = true;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
      if (saved && saved.key === location.search) window.scrollTo(0, saved.y);
      sessionStorage.removeItem(SCROLL_KEY);
    } catch (e) { /* private mode: start at the top */ }
  }, [tail.page, loaded, location.search]);

  const rememberPlace = () => {
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ key: location.search, y: window.scrollY }));
    } catch (e) { /* private mode */ }
  };

  const canLoadMore = tail.page === loaded && tail.hasNext;
  const loadMore = useCallback(() => {
    if (canLoadMore) setQuery({ loaded: String(loaded + 1) }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoadMore, loaded, location.search]);

  const sentinel = useRef(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !canLoadMore) return undefined;
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && loadMore(), {
      rootMargin: '400px',
    });
    io.observe(el);
    return () => io.disconnect();
  }, [canLoadMore, loadMore]);

  const renderCard = s => (
    <SessionCard
      key={s.id}
      s={s}
      isAdmin={isAdmin}
      onOpen={x => {
        rememberPlace();
        onOpen(x);
      }}
      onResume={x => navigate(`/audio-orb-openai?session_id=${x.id}`)}
      onEvaluate={onEvaluate}
    />
  );

  const hasDates = !!(startDate || endDate);

  // No side padding: the page's Container is the gutter, as on 제품 지식 퀴즈. The title is
  // ChatSessionsList's header band.
  return (
    <div style={{ paddingBottom: '40px' }}>
      <div style={{ display: 'flex', gap: '8px' }}>
        <SearchField isMobile value={searchValue} onChange={setSearchValue} onSubmit={() => setQuery({ search: searchValue.trim() })} placeholder="고객, 직원, 대화 내용 검색" />
        <SquareButton icon="sliders horizontal" label="기간 필터" on={filtersOpen} set={hasDates} onClick={() => setFiltersOpen(o => !o)} />
        {/* Admin only: uploading a real store recording for evaluation. A square button beside
            the search, as 직원 관리's 엑셀, so the chip row is the same on every list page. */}
        {isAdmin && <SquareButton icon="upload" label="녹음 파일 업로드" onClick={onUpload} />}
      </div>

      {filtersOpen && (
        <div style={{ marginTop: '8px' }}>
          <PhoneDateRange start={startDate} end={endDate} onChange={(k, v) => setQuery({ [k]: v })} onClear={() => setQuery({ start_date: null, end_date: null })} />
        </div>
      )}

      <ScopeNote linked={LINKED_FILTERS} />
      <ChipRow
        count={tail.count}
        groups={[
          KINDS.map(([label, value]) => (
            <button key={label} type="button" onClick={() => setQuery({ is_practice: value })} style={chip((kind || null) === value)}>
              {label}
            </button>
          )),
        ]}
      />

      <div style={cardStack}>
        {Array.from({ length: loaded }, (_, i) => (
          <Chunk key={`${filterKey}:${i + 1}`} page={i + 1} params={params} onLoaded={onLoaded} renderCard={renderCard} />
        ))}
      </div>

      {canLoadMore && (
        <div ref={sentinel} style={{ textAlign: 'center', marginTop: '16px' }}>
          <button type="button" onClick={loadMore} style={cardAction(false)}>더 보기</button>
        </div>
      )}
      {tail.page === loaded && !tail.hasNext && (
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#86868b' }}>
          {tail.count ? '모든 롤플레이를 불러왔습니다.' : '롤플레이 기록이 없습니다.'}
        </div>
      )}
    </div>
  );
};

export default MobileChatSessionList;
